import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const attempts = useRef(0);
  const { checkAuth } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const kind = params.get("kind");
  const isCredits = kind === "credits";
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); await checkAuth(); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("failed"); return; }
      } catch {}
      if (attempts.current < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-orange-400 animate-spin mx-auto mb-6" />
            <h1 className="font-black text-3xl tracking-tight mb-2">{t("verifying")}</h1>
            <p className="text-zinc-400">{t("verifying_sub")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-orange-400 mx-auto mb-6" />
            <h1 className="font-black text-4xl tracking-tight mb-3">{isCredits ? (t("credits_ok") || "Credits added!") : t("payment_ok")}</h1>
            <p className="text-zinc-400 mb-8">{isCredits ? (t("credits_ok_sub") || "Your credits are ready. Have fun generating!") : t("payment_ok_sub")}</p>
            <Button onClick={() => navigate(isCredits ? "/app/account" : "/app")} className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full h-11 px-8" data-testid="continue-btn">{isCredits ? (t("back_account") || "Back to account") : t("go_generator")}</Button>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-black text-3xl tracking-tight mb-2">{t("payment_issue")}</h1>
            <p className="text-zinc-400 mb-8">{t("payment_issue_sub")}</p>
            <Link to="/app/account"><Button variant="outline" className="rounded-full border-white/20">{t("back_account")}</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#0E0E12] text-white flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <XCircle className="w-16 h-16 text-zinc-500 mx-auto mb-6" />
        <h1 className="font-black text-3xl tracking-tight mb-2">{t("cancelled")}</h1>
        <p className="text-zinc-400 mb-8">{t("cancelled_sub")}</p>
        <Link to="/app/account"><Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full h-11 px-8">{t("back_plans")}</Button></Link>
      </div>
    </div>
  );
}
